import React, { useState, useEffect, useRef, useCallback } from 'react';
import { C } from '../../shared/theme';
import { CLAUDE_HEADERS } from '../../shared/api';
import { playSound, startAmbient, stopAmbient } from '../../shared/soundEngine';

const BASE = process.env.REACT_APP_API_URL || '/api';

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  bg:      '#070c18',
  bg2:     '#0c1220',
  bg3:     '#111827',
  bg4:     '#1a2540',
  bg5:     '#1e2d4a',
  border:  '#1e2d45',
  purple:  '#7C3AED',
  purpleL: '#8B5CF6',
  purpleD: '#5B21B6',
  cyan:    '#06B6D4',
  cyanD:   '#0891B2',
  green:   '#10B981',
  amber:   '#F59E0B',
  red:     '#EF4444',
  slate:   '#64748B',
  slate2:  '#334155',
  white:   '#F1F5F9',
  mono:    '"Courier New", monospace',
};

const LICENSES = ['freemium','individual','starter','athlete','sensei','professional','companies','coaches_solo','coaches_team','coaches_pro'];
const LIC_COLORS = { freemium:'#64748B', individual:'#2A9E82', starter:'#5B9ED0', athlete:'#3B82F6', sensei:'#7B6FD0', professional:'#C8A84B', companies:'#10B981', coaches_solo:'#C06090', coaches_team:'#D4873A', coaches_pro:'#C8A84B' };

const EX_CATS  = ['breathing','mindfulness','recovery','focus','mobility','strength','social'];
const GAME_CATS_LIST = ['memory','reaction','focus','cognition','relaxation','social'];
const ICONS = ['💨','🧘','🌿','⏱️','🚶','🤸','🙏','🧘‍♀️','⬛','🌊','🔥','〰️','💪','👂','🪞','💎','🌱','💗','🧠','⚡','🔢','➕','🌈','🌬️','🎯','🔍','🧭','👁','🔗','📝','🎭','🤝'];

// ── Shared UI ──────────────────────────────────────────────────────────────────
const inp = (extra = {}) => ({
  background: T.bg4, border: `1px solid ${T.border}`, borderRadius: 10,
  color: T.white, fontSize: 14, padding: '11px 14px',
  width: '100%', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
  ...extra,
});

const btn = (variant = 'primary', extra = {}) => ({
  border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700,
  fontSize: 13, padding: '11px 18px', fontFamily: 'inherit',
  ...(variant === 'primary' ? { background: T.purple, color: '#fff' } : {}),
  ...(variant === 'ghost'   ? { background: T.bg4, color: T.slate, border: `1px solid ${T.border}` } : {}),
  ...(variant === 'danger'  ? { background: `${T.red}15`, color: T.red, border: `1px solid ${T.red}33` } : {}),
  ...extra,
});

function Toggle({ value, onChange }) {
  return (
    <button onClick={() => onChange(!value)} style={{
      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
      background: value ? T.purple : T.bg4, position: 'relative', transition: 'background .2s', flexShrink: 0,
    }}>
      <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: value ? 23 : 3, transition: 'left .2s' }} />
    </button>
  );
}

function Badge({ children, color = T.purple }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: '2px 8px', borderRadius: 20,
      background: `${color}20`, color, border: `1px solid ${color}44` }}>
      {children}
    </span>
  );
}

function SectionHead({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 12px' }}>
      <div style={{ flex: 1, height: 1, background: T.border }} />
      <span style={{ fontSize: 10, color: T.slate, fontWeight: 700, letterSpacing: 2, whiteSpace: 'nowrap' }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: T.border }} />
    </div>
  );
}

// ── Content Form (create / edit exercise or game) ──────────────────────────────
function ContentForm({ item, type, uid, onSaved, onClose }) {
  const isEdit = !!item?.id;
  const [form, setForm] = useState(() => item ? { ...item, tags: item.tags || [] } : {
    type, slug: '', title: '', title_nl: '', description: '', description_nl: '',
    icon: type === 'exercise' ? '💨' : '🧠', category: type === 'exercise' ? 'breathing' : 'memory',
    license_required: 'freemium', credit_cost: 0, tags: [],
    duration_mins: type === 'exercise' ? 5 : null, difficulty: 1, is_active: 1,
  });
  const [saving, setSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [err, setErr] = useState('');

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleTag = (tag) => setForm(p => ({
    ...p, tags: p.tags.includes(tag) ? p.tags.filter(t => t !== tag) : [...p.tags, tag],
  }));

  const generateDesc = async () => {
    if (!form.title) { setErr('Add a title first.'); return; }
    setGenLoading(true); setErr('');
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST', headers: CLAUDE_HEADERS,
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001', max_tokens: 400,
          system: `You are a wellness content writer for a mental fitness app. Write a short, engaging description (2-3 sentences, max 60 words) for a ${type}. Be clear, motivating, and scientific. Return plain text only.`,
          messages: [{ role: 'user', content: `${type} title: "${form.title}"\nCategory: ${form.category}\n\nWrite the description in English, then on a new line write "NL:" followed by the Dutch translation.` }],
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || '';
      const [enPart, nlPart] = text.split(/\nNL:/i);
      f('description', enPart?.trim() || text.trim());
      if (nlPart) f('description_nl', nlPart.trim());
    } catch (e) { setErr('AI failed: ' + e.message); }
    setGenLoading(false);
  };

  const save = async () => {
    if (!form.title.trim()) { setErr('Title is required.'); return; }
    setSaving(true); setErr('');
    try {
      const url = isEdit ? `${BASE}/lab/content/${item.id}` : `${BASE}/lab/content`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, uid }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Error'); }
      onSaved();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  const TAG_OPTIONS = ['new','premium','featured','ai-powered','multiplayer','popular'];
  const cats = type === 'exercise' ? EX_CATS : GAME_CATS_LIST;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.bg2, borderRadius: '24px 24px 0 0', padding: '28px 20px 48px', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', borderTop: `2px solid ${T.purple}` }}>
        <div style={{ width: 36, height: 4, background: T.border, borderRadius: 2, margin: '0 auto 24px' }} />
        <div style={{ fontSize: 18, fontWeight: 800, color: T.white, marginBottom: 20 }}>
          {isEdit ? 'Edit' : 'New'} {type === 'exercise' ? 'Exercise' : 'Game'}
        </div>

        {/* Icon picker */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>ICON</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ICONS.map(ic => (
              <button key={ic} onClick={() => f('icon', ic)} style={{ width: 38, height: 38, borderRadius: 10, border: `2px solid ${form.icon === ic ? T.purple : T.border}`, background: form.icon === ic ? `${T.purple}20` : T.bg4, fontSize: 18, cursor: 'pointer' }}>{ic}</button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>TITLE (EN) *</div>
            <input style={inp()} value={form.title} onChange={e => f('title', e.target.value)} placeholder="e.g. Box Breathing" />
          </div>
          <div>
            <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>TITLE (NL)</div>
            <input style={inp()} value={form.title_nl || ''} onChange={e => f('title_nl', e.target.value)} placeholder="e.g. Vierkante Adem" />
          </div>
        </div>

        {/* Slug */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>SLUG (ID) — leave empty to auto-generate</div>
          <input style={inp()} value={form.slug || ''} onChange={e => f('slug', e.target.value)} placeholder="e.g. box_breathing" />
        </div>

        {/* Category */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>CATEGORY</div>
          <select style={inp()} value={form.category || ''} onChange={e => f('category', e.target.value)}>
            {cats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Description */}
        <div style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1 }}>DESCRIPTION (EN)</span>
            <button onClick={generateDesc} disabled={genLoading} style={{ ...btn('ghost', { fontSize: 11, padding: '5px 12px', borderRadius: 8 }), color: T.purpleL, borderColor: T.purpleL + '55' }}>
              {genLoading ? '…' : '✦ AI Generate'}
            </button>
          </div>
          <textarea style={{ ...inp(), minHeight: 80, resize: 'vertical' }} value={form.description || ''} onChange={e => f('description', e.target.value)} placeholder="Describe this exercise or game…" />
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>DESCRIPTION (NL)</div>
          <textarea style={{ ...inp(), minHeight: 60, resize: 'vertical' }} value={form.description_nl || ''} onChange={e => f('description_nl', e.target.value)} placeholder="Beschrijving in het Nederlands…" />
        </div>

        {/* License + Credits */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>LICENSE REQUIRED</div>
            <select style={inp()} value={form.license_required || 'freemium'} onChange={e => f('license_required', e.target.value)}>
              {LICENSES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>CREDIT COST</div>
            <input type="number" min="0" style={inp()} value={form.credit_cost ?? 0} onChange={e => f('credit_cost', parseInt(e.target.value) || 0)} />
          </div>
        </div>

        {/* Duration + Difficulty (exercises only) */}
        {type === 'exercise' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>DURATION (mins)</div>
              <input type="number" min="1" max="60" style={inp()} value={form.duration_mins || ''} onChange={e => f('duration_mins', parseInt(e.target.value) || null)} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>DIFFICULTY (1–5)</div>
              <select style={inp()} value={form.difficulty || 1} onChange={e => f('difficulty', parseInt(e.target.value))}>
                {[1,2,3,4,5].map(d => <option key={d} value={d}>{['★☆☆☆☆','★★☆☆☆','★★★☆☆','★★★★☆','★★★★★'][d-1]} {d}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* Tags */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: T.slate, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>LABELS</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {TAG_OPTIONS.map(tag => {
              const active = form.tags?.includes(tag);
              const colors = { new: T.green, premium: T.amber, featured: T.cyan, 'ai-powered': T.purpleL, multiplayer: '#E879F9', popular: T.amber };
              const col = colors[tag] || T.slate;
              return (
                <button key={tag} onClick={() => toggleTag(tag)} style={{ padding: '5px 12px', borderRadius: 20, border: `1px solid ${active ? col + '88' : T.border}`, background: active ? `${col}20` : T.bg4, color: active ? col : T.slate, fontSize: 11, fontWeight: active ? 700 : 400, cursor: 'pointer' }}>
                  {tag}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px', background: T.bg4, borderRadius: 12, border: `1px solid ${T.border}`, marginBottom: 20 }}>
          <span style={{ fontSize: 13, color: T.white, fontWeight: 600 }}>Active in app</span>
          <Toggle value={!!form.is_active} onChange={v => f('is_active', v ? 1 : 0)} />
        </div>

        {err && <div style={{ color: T.red, fontSize: 12, marginBottom: 12 }}>⚠ {err}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
          <button onClick={onClose} style={btn('ghost', { padding: '13px 0' })}>Cancel</button>
          <button onClick={save} disabled={saving} style={btn('primary', { padding: '13px 0', opacity: saving ? 0.7 : 1 })}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : `Create ${type === 'exercise' ? 'Exercise' : 'Game'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Content Card ───────────────────────────────────────────────────────────────
function ContentCard({ item, isResearcher, onEdit, onDelete, onToggleActive }) {
  const licColor = LIC_COLORS[item.license_required] || T.slate;
  const tagColors = { new: T.green, premium: T.amber, featured: T.cyan, 'ai-powered': T.purpleL, multiplayer: '#E879F9', popular: T.amber };

  return (
    <div style={{ background: T.bg3, border: `1px solid ${item.is_active ? T.border : T.border + '66'}`, borderRadius: 16, padding: '16px', opacity: item.is_active ? 1 : 0.55, transition: 'opacity .2s', position: 'relative', overflow: 'hidden' }}>
      {!item.is_active && (
        <div style={{ position: 'absolute', top: 10, right: 10, fontSize: 9, color: T.slate, background: T.bg4, borderRadius: 4, padding: '2px 6px', fontWeight: 700, letterSpacing: 1 }}>INACTIVE</div>
      )}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ width: 46, height: 46, borderRadius: 14, background: T.bg4, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
          {item.icon || '⚡'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.white, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
          {item.title_nl && <div style={{ fontSize: 11, color: T.slate, marginBottom: 4 }}>{item.title_nl}</div>}
          {item.description && <div style={{ fontSize: 12, color: T.slate, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.description}</div>}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <Badge color={licColor}>{item.license_required}</Badge>
        {(item.credit_cost ?? 0) > 0 && <Badge color={T.amber}>💎 {item.credit_cost} cr</Badge>}
        {item.category && <Badge color={T.cyan}>{item.category}</Badge>}
        {(item.tags || []).map(tag => <Badge key={tag} color={tagColors[tag] || T.slate}>{tag}</Badge>)}
      </div>

      {item.duration_mins && (
        <div style={{ fontSize: 11, color: T.slate, marginBottom: 8 }}>⏱ {item.duration_mins} min{item.difficulty ? ` · ${'★'.repeat(item.difficulty)}${'☆'.repeat(5 - item.difficulty)}` : ''}</div>
      )}

      {isResearcher && (
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <button onClick={() => onToggleActive(item)} style={{ ...btn('ghost', { flex: 1, fontSize: 11, padding: '7px 0', borderRadius: 8 }), color: item.is_active ? T.slate : T.green }}>
            {item.is_active ? 'Disable' : 'Enable'}
          </button>
          <button onClick={() => onEdit(item)} style={btn('ghost', { flex: 1, fontSize: 11, padding: '7px 0', borderRadius: 8 })}>Edit</button>
          <button onClick={() => onDelete(item)} style={btn('danger', { flex: 1, fontSize: 11, padding: '7px 0', borderRadius: 8 })}>Delete</button>
        </div>
      )}
    </div>
  );
}

// ── Content Tab (exercises or games) ──────────────────────────────────────────
function ContentTab({ type, uid, isResearcher }) {
  const [items, setItems] = useState(null);
  const [formItem, setFormItem] = useState(null); // null | 'new' | item
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/lab/content?type=${type}`);
      if (r.ok) setItems(await r.json());
    } catch {}
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    await fetch(`${BASE}/lab/content/${item.id}?uid=${encodeURIComponent(uid)}`, { method: 'DELETE' });
    load();
  };

  const toggleActive = async (item) => {
    await fetch(`${BASE}/lab/content/${item.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, is_active: item.is_active ? 0 : 1 }),
    });
    load();
  };

  const cats = type === 'exercise' ? EX_CATS : GAME_CATS_LIST;
  const filtered = (items || []).filter(it =>
    (filter === 'all' || it.category === filter) &&
    (!search || it.title?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div>
      {isResearcher && (
        <button onClick={() => setFormItem('new')} style={{ ...btn('primary', { width: '100%', marginBottom: 16, padding: '13px 0', borderRadius: 12 }), background: `linear-gradient(135deg, ${T.purple}, ${T.purpleD})` }}>
          + New {type === 'exercise' ? 'Exercise' : 'Game'}
        </button>
      )}

      {/* Search + Filter */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${type}s…`}
          style={{ ...inp(), paddingLeft: 40 }} />
        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.slate, fontSize: 16 }}>⌕</span>
      </div>

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 12 }}>
        {['all', ...cats].map(c => (
          <button key={c} onClick={() => setFilter(c)} style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 20, border: `1px solid ${filter === c ? T.purple + '88' : T.border}`, background: filter === c ? `${T.purple}20` : T.bg4, color: filter === c ? T.purpleL : T.slate, fontSize: 12, fontWeight: filter === c ? 700 : 400, cursor: 'pointer' }}>
            {c}
          </button>
        ))}
      </div>

      {items === null ? (
        <div style={{ textAlign: 'center', padding: 48, color: T.slate }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: T.slate }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>{type === 'exercise' ? '🧘' : '🎮'}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.white, marginBottom: 6 }}>No {type}s yet</div>
          {isResearcher && <div style={{ fontSize: 12 }}>Create your first {type} using the button above</div>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(item => (
            <ContentCard key={item.id} item={item} isResearcher={isResearcher}
              onEdit={setFormItem} onDelete={del} onToggleActive={toggleActive} />
          ))}
        </div>
      )}

      {formItem !== null && (
        <ContentForm
          item={formItem === 'new' ? null : formItem}
          type={type}
          uid={uid}
          onSaved={() => { setFormItem(null); load(); }}
          onClose={() => setFormItem(null)}
        />
      )}
    </div>
  );
}

// ── Audio specimen player ──────────────────────────────────────────────────────
const PROG_CATS = [
  { id: 'general',    label: 'General',    labelNL: 'Algemeen',   icon: '⬡' },
  { id: 'strength',   label: 'Strength',   labelNL: 'Kracht',     icon: '⬟' },
  { id: 'mobility',   label: 'Mobility',   labelNL: 'Mobiliteit', icon: '⬠' },
  { id: 'mindfulness',label: 'Mindfulness',labelNL: 'Mindfulness',icon: '⬢' },
  { id: 'cardio',     label: 'Cardio',     labelNL: 'Cardio',     icon: '◉' },
];
const TRACK_CATS = [
  { id: 'focus',      label: 'Focus',     labelNL: 'Focus',   color: '#5B9ED0' },
  { id: 'energy',     label: 'Energy',    labelNL: 'Energie', color: '#D4873A' },
  { id: 'calm',       label: 'Calm',      labelNL: 'Kalmte',  color: T.cyan    },
  { id: 'motivation', label: 'Motivation',labelNL: 'Motivatie',color: T.amber  },
  { id: 'ambient',    label: 'Ambient',   labelNL: 'Ambient', color: T.purpleL },
];

function fmtDur(secs) {
  if (!secs) return '';
  return `${Math.floor(secs/60)}:${String(secs%60).padStart(2,'0')}`;
}

function SpecimenPlayer({ track, activeId, onActivate, canPlay, lang, uid }) {
  const nl = lang === 'nl';
  const audioRef = useRef(null);
  const [playing, setPlaying]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [rating, setRating]     = useState(0);
  const isActive = activeId === track.id;
  const cat = TRACK_CATS.find(c => c.id === track.category) || TRACK_CATS[0];

  useEffect(() => {
    if (!isActive && playing) { audioRef.current?.pause(); setPlaying(false); }
  }, [isActive, playing]);

  const toggle = () => {
    if (!canPlay) return;
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); stopAmbient(); }
    else {
      onActivate(track.id);
      a.play().then(() => setPlaying(true)).catch(() => {});
      startAmbient('lab');
    }
  };

  const submitRating = async (stars) => {
    setRating(stars);
    try {
      await fetch(`${BASE}/lab/tracks/${track.id}/rate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, rating: stars }),
      });
    } catch {}
  };

  return (
    <div style={{ background: T.bg3, border: `1px solid ${isActive && playing ? cat.color + '55' : T.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 8, transition: 'border-color .3s' }}>
      {track.file_url && <audio ref={audioRef} src={track.file_url} onTimeUpdate={e => setProgress(e.target.duration ? e.target.currentTime / e.target.duration : 0)} onEnded={() => { setPlaying(false); stopAmbient(); }} />}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button onClick={toggle} style={{ width: 44, height: 44, borderRadius: '50%', background: canPlay ? (playing ? `${cat.color}33` : T.bg4) : T.bg4, border: `2px solid ${canPlay ? cat.color : T.border}`, cursor: canPlay ? 'pointer' : 'default', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {canPlay ? (playing ? '⏸' : '▶') : '🔒'}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{track.title}</span>
            <span style={{ fontSize: 11, color: T.slate, flexShrink: 0, marginLeft: 8 }}>{fmtDur(track.duration_secs)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: playing ? 6 : 0 }}>
            <span style={{ fontSize: 10, color: cat.color, fontWeight: 700 }}>{nl ? TRACK_CATS.find(c=>c.id===track.category)?.labelNL : cat.label}</span>
            {track.artist && <span style={{ fontSize: 10, color: T.slate }}>· {track.artist}</span>}
          </div>
          {playing && (
            <div style={{ height: 3, background: T.bg4, borderRadius: 2 }}>
              <div style={{ width: `${progress * 100}%`, height: '100%', background: cat.color, borderRadius: 2, transition: 'width 1s linear' }} />
            </div>
          )}
        </div>
      </div>
      {canPlay && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 10 }}>
          <span style={{ fontSize: 10, color: T.slate }}>Rate:</span>
          {[1,2,3,4,5].map(s => (
            <button key={s} onClick={() => submitRating(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: s <= rating ? T.amber : T.slate, padding: 0 }}>★</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Track Upload Form ──────────────────────────────────────────────────────────
function TrackUploadForm({ uid, lang, onUploaded }) {
  const nl = lang === 'nl';
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', artist: '', category: 'focus', file_url: '', duration_secs: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [fileName, setFileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  if (!open) return (
    <button onClick={() => setOpen(true)} style={btn('ghost', { width: '100%', marginBottom: 12, padding: '11px 0', borderRadius: 12 })}>
      + {nl ? 'Specimen toevoegen' : 'Add Specimen'}
    </button>
  );

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setErr('');
    setUploading(true); setUploadPct(10);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('uid', uid);
      // Fake progress ticks while XHR runs
      const tick = setInterval(() => setUploadPct(p => Math.min(p + 15, 85)), 300);
      const res = await fetch(`${BASE}/lab/tracks/upload`, { method: 'POST', body: fd });
      clearInterval(tick); setUploadPct(100);
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Upload failed'); }
      const { url } = await res.json();
      f('file_url', url);
      // Auto-set duration from audio metadata
      const audio = new Audio(url);
      audio.addEventListener('loadedmetadata', () => {
        if (audio.duration && isFinite(audio.duration)) f('duration_secs', Math.round(audio.duration).toString());
      });
      // Auto-set title from filename if empty
      if (!form.title) f('title', file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
    } catch (e) { setErr(e.message); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.title || !form.file_url) { setErr(nl ? 'Titel en audiobestand verplicht.' : 'Title and audio file required.'); return; }
    setSaving(true); setErr('');
    try {
      const res = await fetch(`${BASE}/lab/tracks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, uid, duration_secs: parseInt(form.duration_secs) || 0 }),
      });
      if (!res.ok) throw new Error('Error');
      setOpen(false);
      setForm({ title: '', artist: '', category: 'focus', file_url: '', duration_secs: '' });
      setFileName(''); setUploadPct(0);
      onUploaded();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.white, marginBottom: 14 }}>
        🎵 {nl ? 'Specimen toevoegen' : 'Add Specimen'}
      </div>

      {/* Audio file picker */}
      <input ref={fileRef} type="file" accept="audio/*,.mp3,.wav,.ogg,.aac,.m4a,.flac,.webm" style={{ display: 'none' }} onChange={handleFile} />
      <div
        onClick={() => !uploading && fileRef.current?.click()}
        style={{
          border: `2px dashed ${form.file_url ? T.green || '#22c55e' : T.border}`,
          borderRadius: 10, padding: '16px 12px', marginBottom: 10,
          textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
          background: form.file_url ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.02)',
          transition: 'all 0.2s',
        }}
      >
        {uploading ? (
          <div>
            <div style={{ fontSize: 12, color: T.silver, marginBottom: 6 }}>{nl ? 'Uploaden…' : 'Uploading…'} {uploadPct}%</div>
            <div style={{ height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${uploadPct}%`, background: 'linear-gradient(90deg,#5B9ED0,#2A9E82)', borderRadius: 4, transition: 'width 0.3s ease' }}/>
            </div>
          </div>
        ) : form.file_url ? (
          <div>
            <div style={{ fontSize: 20, marginBottom: 4 }}>✅</div>
            <div style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>{fileName || (nl ? 'Bestand geladen' : 'File loaded')}</div>
            <div style={{ fontSize: 10, color: T.silver, marginTop: 2 }}>{nl ? 'Klik om te vervangen' : 'Click to replace'}</div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 28, marginBottom: 4 }}>🎵</div>
            <div style={{ fontSize: 12, color: T.silver, fontWeight: 600 }}>{nl ? 'Klik om audiobestand te kiezen' : 'Click to choose audio file'}</div>
            <div style={{ fontSize: 10, color: `${T.silver}88`, marginTop: 2 }}>MP3 · WAV · OGG · AAC · M4A · max 80 MB</div>
          </div>
        )}
      </div>

      <input style={{ ...inp(), marginBottom: 10 }} placeholder={`${nl ? 'Titel' : 'Title'} *`} value={form.title} onChange={e => f('title', e.target.value)} />
      <input style={{ ...inp(), marginBottom: 10 }} placeholder={nl ? 'Artiest / Maker' : 'Artist / Creator'} value={form.artist} onChange={e => f('artist', e.target.value)} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <select style={inp()} value={form.category} onChange={e => f('category', e.target.value)}>
          {TRACK_CATS.map(c => <option key={c.id} value={c.id}>{nl ? c.labelNL : c.label}</option>)}
        </select>
        <input style={inp()} type="number" placeholder={nl ? 'Duur (sec)' : 'Duration (secs)'} value={form.duration_secs} onChange={e => f('duration_secs', e.target.value)} />
      </div>

      {err && <div style={{ color: T.red, fontSize: 12, marginBottom: 10 }}>⚠ {err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button style={btn('ghost', { padding: '11px 0', borderRadius: 10 })} onClick={() => { setOpen(false); setFileName(''); setUploadPct(0); }}>{nl ? 'Annuleren' : 'Cancel'}</button>
        <button style={btn('primary', { padding: '11px 0', borderRadius: 10 })} onClick={save} disabled={saving || uploading || !form.file_url}>
          {saving ? '…' : nl ? 'Opslaan' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── AI Protocol Form ───────────────────────────────────────────────────────────
function AIProtocolForm({ uid, lang, tracks, onCreated }) {
  const nl = lang === 'nl';
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [category, setCategory] = useState('general');
  const [weeks, setWeeks] = useState(4);
  const [trackId, setTrackId] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  if (!open) return (
    <button onClick={() => setOpen(true)} style={{ ...btn('primary', { width: '100%', marginBottom: 10, padding: '13px 0', borderRadius: 12 }), background: `linear-gradient(135deg, ${T.purpleL}, ${T.purple})` }}>
      ✦ {nl ? 'AI Protocol Genereren' : 'Generate with AI'}
    </button>
  );

  const generate = async () => {
    if (!prompt.trim()) { setErr('Describe the protocol.'); return; }
    setLoading(true); setErr('');
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST', headers: CLAUDE_HEADERS,
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001', max_tokens: 500,
          system: 'You are a wellness program designer. Create a structured training protocol as JSON with fields: title (string), description (string), exercises (array of {week,day,name,sets,reps}). Max 5 exercises. Return ONLY valid JSON.',
          messages: [{ role: 'user', content: `Create a ${weeks}-week ${category} protocol: ${prompt}` }],
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text?.trim() || '';
      const parsed = JSON.parse(text.replace(/^```json\n?/, '').replace(/\n?```$/, ''));
      await fetch(`${BASE}/lab/programs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, ...parsed, category, is_ai: true, track_id: trackId ? parseInt(trackId) : null, duration_weeks: weeks, published: true }),
      });
      setOpen(false); setPrompt(''); onCreated();
    } catch (e) { setErr('AI failed: ' + e.message); }
    setLoading(false);
  };

  return (
    <div style={{ background: T.bg3, border: `1px solid ${T.purpleL}44`, borderRadius: 14, padding: 16, marginBottom: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.white, marginBottom: 14 }}>✦ AI Protocol Generator</div>
      <textarea style={{ ...inp(), minHeight: 76, resize: 'vertical', marginBottom: 10 }}
        placeholder={nl ? 'Beschrijf het protocol…' : 'Describe the protocol goal, level, focus…'}
        value={prompt} onChange={e => setPrompt(e.target.value)} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {PROG_CATS.map(c => (
          <button key={c.id} onClick={() => setCategory(c.id)} style={{ padding: '5px 12px', borderRadius: 20, border: `1px solid ${category === c.id ? T.purpleL + '88' : T.border}`, background: category === c.id ? `${T.purpleL}20` : T.bg4, color: category === c.id ? T.purpleL : T.slate, fontSize: 11, cursor: 'pointer' }}>
            {c.icon} {nl ? c.labelNL : c.label}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <select style={inp()} value={weeks} onChange={e => setWeeks(parseInt(e.target.value))}>
          {[2,4,6,8,12].map(w => <option key={w} value={w}>{w} weeks</option>)}
        </select>
        <select style={inp()} value={trackId} onChange={e => setTrackId(e.target.value)}>
          <option value="">— no specimen —</option>
          {tracks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
      </div>
      {err && <div style={{ color: T.red, fontSize: 12, marginBottom: 10 }}>⚠ {err}</div>}
      {loading && <div style={{ color: T.purpleL, fontSize: 12, textAlign: 'center', marginBottom: 10 }}>✦ Synthesising…</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
        <button style={btn('ghost', { borderRadius: 10 })} onClick={() => setOpen(false)}>Cancel</button>
        <button style={{ ...btn('primary', { borderRadius: 10 }), background: `${T.purpleL}cc` }} onClick={generate} disabled={loading}>{loading ? '…' : 'Generate →'}</button>
      </div>
    </div>
  );
}

// ── Manual Protocol Form with Schedule Builder ─────────────────────────────────
const SCHED_EXERCISES = [
  { id: 'breathing', label: '💨 Breathing' }, { id: 'boxbreathing', label: '⬛ Box Breathing' },
  { id: 'breathing478', label: '🌊 4-7-8 Breathing' }, { id: 'wimhof', label: '🔥 Power Breathing' },
  { id: 'coherent', label: '〰️ Coherent Breathing' }, { id: 'meditation', label: '🧘 Meditation' },
  { id: 'bodyscan', label: '🌿 Body Scan' }, { id: 'focus', label: '⏱️ Focus Timer' },
  { id: 'walking', label: '🚶 Walking' }, { id: 'stretching', label: '🤸 Stretching' },
  { id: 'gratitude', label: '🙏 Gratitude' }, { id: 'yoga', label: '🧘‍♀️ Yoga' },
  { id: 'powerpose', label: '💪 Power Pose' }, { id: 'conscious_listening', label: '👂 Conscious Listening' },
  { id: 'perspective_mirror', label: '🪞 Perspective Mirror' }, { id: 'emotional_clarity', label: '💎 Emotional Clarity' },
  { id: 'grounding_541', label: '🌱 5-4-3-2-1 Grounding' }, { id: 'emotion_reg', label: '💗 Emotion Regulation' },
];

function ManualProtocolForm({ uid, lang, tracks, onCreated }) {
  const nl = lang === 'nl';
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [category, setCategory] = useState('general');
  const [trackId, setTrackId] = useState('');
  const [weeks, setWeeks] = useState(4);
  const [sessions, setSessions] = useState([
    { label: 'Session A', exIds: [] },
    { label: 'Session B', exIds: [] },
    { label: 'Session C', exIds: [] },
  ]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  if (!open) return (
    <button style={btn('ghost', { width: '100%', marginBottom: 10, padding: '11px 0', borderRadius: 12 })} onClick={() => setOpen(true)}>
      + {nl ? 'Handmatig Protocol' : 'Manual Protocol'}
    </button>
  );

  const toggleEx = (sIdx, exId) => setSessions(prev => prev.map((s, i) => i !== sIdx ? s : {
    ...s, exIds: s.exIds.includes(exId) ? s.exIds.filter(e => e !== exId) : [...s.exIds, exId],
  }));

  const submit = async () => {
    if (!title.trim()) { setErr('Title required.'); return; }
    setLoading(true); setErr('');
    const exercises = sessions.filter(s => s.exIds.length > 0).map((s, i) => ({ label: s.label, exIds: s.exIds, dayIndex: i }));
    try {
      const res = await fetch(`${BASE}/lab/programs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, title, description: desc, category, is_ai: false, exercises, track_id: trackId ? parseInt(trackId) : null, duration_weeks: weeks, published: true }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Error'); }
      setTitle(''); setDesc(''); setOpen(false); onCreated();
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.white, marginBottom: 14 }}>Manual Protocol</div>
      <input style={{ ...inp(), marginBottom: 10 }} placeholder="Protocol title *" value={title} onChange={e => setTitle(e.target.value)} />
      <textarea style={{ ...inp(), minHeight: 56, resize: 'vertical', marginBottom: 10 }} placeholder="Description" value={desc} onChange={e => setDesc(e.target.value)} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <select style={inp()} value={category} onChange={e => setCategory(e.target.value)}>
          {PROG_CATS.map(c => <option key={c.id} value={c.id}>{c.icon} {nl ? c.labelNL : c.label}</option>)}
        </select>
        <select style={inp()} value={trackId} onChange={e => setTrackId(e.target.value)}>
          <option value="">— no specimen —</option>
          {tracks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
      </div>
      <select style={{ ...inp(), marginBottom: 14 }} value={weeks} onChange={e => setWeeks(parseInt(e.target.value))}>
        {[2,4,6,8,12].map(w => <option key={w} value={w}>{w} weeks</option>)}
      </select>

      <SectionHead>EXERCISE SCHEDULE — REPEATS WEEKLY</SectionHead>
      {sessions.map((sess, sIdx) => (
        <div key={sIdx} style={{ background: T.bg4, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input style={{ ...inp(), flex: 1 }} value={sess.label} onChange={e => setSessions(p => p.map((s, i) => i === sIdx ? { ...s, label: e.target.value } : s))} />
            {sessions.length > 1 && <button onClick={() => setSessions(p => p.filter((_, i) => i !== sIdx))} style={{ background: 'none', border: 'none', color: T.red, cursor: 'pointer', fontSize: 18, padding: '0 4px' }}>✕</button>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {SCHED_EXERCISES.map(ex => {
              const on = sess.exIds.includes(ex.id);
              return <button key={ex.id} onClick={() => toggleEx(sIdx, ex.id)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, cursor: 'pointer', background: on ? `${T.cyan}20` : T.bg3, border: `1px solid ${on ? T.cyan + '88' : T.border}`, color: on ? T.cyan : T.slate }}>{ex.label}</button>;
            })}
          </div>
          {sess.exIds.length > 0 && <div style={{ fontSize: 10, color: T.green, marginTop: 6 }}>✓ {sess.exIds.length} selected</div>}
        </div>
      ))}
      <button style={btn('ghost', { width: '100%', marginBottom: 12, fontSize: 12, padding: '9px 0', borderRadius: 10 })}
        onClick={() => setSessions(p => [...p, { label: `Session ${String.fromCharCode(65 + p.length)}`, exIds: [] }])}>
        + Add Session
      </button>

      {err && <div style={{ color: T.red, fontSize: 12, marginBottom: 10 }}>⚠ {err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <button style={btn('ghost', { borderRadius: 10 })} onClick={() => setOpen(false)}>Cancel</button>
        <button style={btn('primary', { borderRadius: 10 })} onClick={submit} disabled={loading}>{loading ? '…' : 'Save →'}</button>
      </div>
    </div>
  );
}

// ── Protocol Card ──────────────────────────────────────────────────────────────
function ProtocolCard({ prog, uid, lang, tracks, onEnrolled, onProgress, canEnroll, isResearcher }) {
  const nl = lang === 'nl';
  const [enrolling, setEnrolling]   = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assignQuery, setAssignQuery] = useState('');
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignDone, setAssignDone]   = useState(null);
  const cat = PROG_CATS.find(c => c.id === prog.category) || PROG_CATS[0];
  const linkedTrack = tracks.find(t => t.id === prog.track_id);
  const isEnrolled = prog.my_progress != null;

  const enroll = async () => {
    setEnrolling(true);
    try {
      await fetch(`${BASE}/lab/programs/${prog.id}/enroll`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ uid }),
      });
      onEnrolled();
    } catch {}
    setEnrolling(false);
  };

  const updateProgress = async (pct) => {
    try {
      await fetch(`${BASE}/lab/programs/${prog.id}/progress`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ uid, progress: pct }),
      });
      onProgress(prog.id, pct);
    } catch {}
  };

  const searchUsers = async (q) => {
    setAssignQuery(q);
    if (!q || q.length < 2) { setAssignUsers([]); return; }
    setAssignLoading(true);
    try { const r = await fetch(`${BASE}/lab/users?uid=${encodeURIComponent(uid)}&q=${encodeURIComponent(q)}`); if (r.ok) setAssignUsers(await r.json()); } catch {}
    setAssignLoading(false);
  };

  const assignTo = async (targetUser) => {
    try {
      await fetch(`${BASE}/lab/programs/${prog.id}/assign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, target_user_id: targetUser.id }),
      });
      setAssignDone(targetUser.name || targetUser.email);
      setAssignUsers([]); setAssignQuery('');
      setTimeout(() => { setAssignDone(null); setShowAssign(false); }, 2500);
    } catch {}
  };

  return (
    <div style={{ background: T.bg3, border: `1px solid ${isEnrolled ? T.cyan + '55' : T.border}`, borderRadius: 14, padding: '16px', marginBottom: 8, position: 'relative', overflow: 'hidden' }}>
      {isEnrolled && <div style={{ position: 'absolute', top: 0, left: 0, width: `${prog.my_progress}%`, height: 2, background: `linear-gradient(90deg, ${T.purple}, ${T.cyan})` }} />}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: T.bg4, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
          {cat.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            {prog.is_ai === 1 && <Badge color={T.purpleL}>AI</Badge>}
            {isEnrolled && <Badge color={T.cyan}>ACTIVE</Badge>}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.white }}>{prog.title}</div>
          {prog.description && <div style={{ fontSize: 12, color: T.slate, marginTop: 3, lineHeight: 1.5 }}>{prog.description}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: T.slate }}>📅 {prog.duration_weeks}w</span>
            {linkedTrack && <span style={{ fontSize: 11, color: T.cyan }}>🎵 {linkedTrack.title}</span>}
            {prog.enrolled_count > 0 && <span style={{ fontSize: 11, color: T.slate }}>👥 {prog.enrolled_count}</span>}
          </div>
        </div>
        <button onClick={() => setShowDetail(d => !d)} style={{ background: 'none', border: 'none', color: T.slate, cursor: 'pointer', fontSize: 16, padding: 4 }}>
          {showDetail ? '▲' : '▼'}
        </button>
      </div>

      {isEnrolled && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: T.slate }}>Completion</span>
            <span style={{ fontSize: 11, color: T.cyan, fontWeight: 700 }}>{prog.my_progress}%</span>
          </div>
          <div style={{ height: 4, background: T.bg4, borderRadius: 2, marginBottom: 8 }}>
            <div style={{ width: `${prog.my_progress}%`, height: '100%', background: `linear-gradient(90deg, ${T.purple}, ${T.cyan})`, borderRadius: 2 }} />
          </div>
          {prog.my_progress < 100 && (
            <div style={{ display: 'flex', gap: 6 }}>
              {[25,50,75,100].filter(p => p > prog.my_progress).slice(0,3).map(p => (
                <button key={p} style={btn('ghost', { flex: 1, fontSize: 11, padding: '6px 0', borderRadius: 8 })} onClick={() => updateProgress(p)}>{p}%</button>
              ))}
            </div>
          )}
        </div>
      )}

      {showDetail && prog.exercises?.length > 0 && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
          <div style={{ fontSize: 11, color: T.cyan, fontWeight: 700, marginBottom: 8 }}>Schedule</div>
          {Object.entries(
            prog.exercises.reduce((acc, ex) => { const k = `W${ex.week}`; if (!acc[k]) acc[k]=[]; acc[k].push(ex); return acc; }, {})
          ).slice(0,3).map(([week, exs]) => (
            <div key={week} style={{ marginBottom: 8 }}>
              <div style={{ color: T.cyan, fontSize: 10, fontWeight: 700, marginBottom: 3 }}>{week}</div>
              {exs.slice(0,4).map((ex, i) => <div key={i} style={{ color: T.slate, fontSize: 11 }}>D{ex.day}: {ex.name} — {ex.sets}×{ex.reps}</div>)}
            </div>
          ))}
        </div>
      )}

      {!isEnrolled && canEnroll && (
        <button style={btn('primary', { width: '100%', marginTop: 12, borderRadius: 10 })} onClick={enroll} disabled={enrolling}>
          {enrolling ? '…' : '→ Enroll'}
        </button>
      )}
      {!isEnrolled && !canEnroll && (
        <div style={{ marginTop: 12, padding: '10px', background: T.bg4, borderRadius: 8, border: `1px solid ${T.border}`, textAlign: 'center', fontSize: 12, color: T.slate }}>
          🔒 Upgrade to enroll
        </div>
      )}

      {isResearcher && (
        <div style={{ marginTop: 10 }}>
          {!showAssign ? (
            <button style={btn('ghost', { width: '100%', fontSize: 11, padding: '8px 0', borderRadius: 8 })} onClick={() => setShowAssign(true)}>
              Assign to User
            </button>
          ) : (
            <div style={{ background: T.bg4, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
              {assignDone ? (
                <div style={{ color: T.green, fontSize: 12, textAlign: 'center', padding: '8px 0', fontWeight: 700 }}>✓ Assigned to {assignDone}</div>
              ) : (
                <>
                  <input style={{ ...inp(), marginBottom: 8 }} placeholder="Search user by name or email…"
                    value={assignQuery} onChange={e => searchUsers(e.target.value)} />
                  {assignLoading && <div style={{ fontSize: 11, color: T.slate, marginBottom: 6 }}>Searching…</div>}
                  {assignUsers.length > 0 && (
                    <div style={{ maxHeight: 160, overflowY: 'auto', marginBottom: 8 }}>
                      {assignUsers.map(u => (
                        <button key={u.id} onClick={() => assignTo(u)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', marginBottom: 4, textAlign: 'left' }}>
                          <span style={{ fontSize: 12, color: T.white, fontWeight: 600, flex: 1 }}>{u.name || '—'}</span>
                          <span style={{ fontSize: 11, color: T.slate }}>{u.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <button style={btn('ghost', { fontSize: 11, padding: '6px 14px', borderRadius: 8 })} onClick={() => { setShowAssign(false); setAssignQuery(''); setAssignUsers([]); }}>Cancel</button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Research Data ──────────────────────────────────────────────────────────────
function ResearchData({ tracks, programs, lang }) {
  const nl = lang === 'nl';
  const [trackAnalysis, setTrackAnalysis] = useState({});
  const [progAnalysis, setProgAnalysis]   = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      const ta = {}, pa = {};
      await Promise.all([
        ...tracks.map(async t => { try { const r = await fetch(`${BASE}/lab/tracks/${t.id}/analysis`); if (r.ok) ta[t.id] = await r.json(); } catch {} }),
        ...programs.map(async p => { try { const r = await fetch(`${BASE}/lab/programs/${p.id}/analysis`); if (r.ok) pa[p.id] = await r.json(); } catch {} }),
      ]);
      setTrackAnalysis(ta); setProgAnalysis(pa); setLoading(false);
    }
    if (tracks.length || programs.length) loadAll(); else setLoading(false);
  }, [tracks, programs]); // eslint-disable-line

  if (loading) return <div style={{ textAlign: 'center', padding: 48, color: T.slate }}>Loading data…</div>;

  const tracksWithData = tracks.filter(t => trackAnalysis[t.id]?.total_ratings > 0);
  const progsWithData  = programs.filter(p => progAnalysis[p.id]?.total_enrolled > 0);

  if (!tracksWithData.length && !progsWithData.length)
    return <div style={{ textAlign: 'center', padding: 48, color: T.slate }}>No research data yet.</div>;

  return (
    <div>
      {tracksWithData.map(t => {
        const a = trackAnalysis[t.id];
        const cat = TRACK_CATS.find(c => c.id === t.category) || TRACK_CATS[0];
        return (
          <div key={t.id} style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div><Badge color={cat.color}>{cat.label}</Badge><div style={{ fontSize: 14, fontWeight: 700, color: T.white, marginTop: 4 }}>{t.title}</div></div>
              <div style={{ textAlign: 'right' }}><div style={{ fontSize: 28, fontWeight: 800, color: T.cyan }}>{a.avg_rating?.toFixed(1)}</div><div style={{ fontSize: 10, color: T.slate }}>{a.total_ratings} ratings</div></div>
            </div>
            {[5,4,3,2,1].map(s => { const c = a.distribution?.[s] || 0; const pct = a.total_ratings ? (c/a.total_ratings)*100 : 0;
              return <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                <span style={{ fontSize: 10, color: T.slate, width: 10 }}>{s}</span>
                <div style={{ flex: 1, height: 3, background: T.bg4, borderRadius: 2 }}><div style={{ width: `${pct}%`, height: '100%', background: cat.color, borderRadius: 2 }} /></div>
                <span style={{ fontSize: 10, color: T.slate, width: 16 }}>{c}</span>
              </div>;
            })}
          </div>
        );
      })}
      {progsWithData.map(p => {
        const a = progAnalysis[p.id];
        return (
          <div key={p.id} style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.white, marginBottom: 10 }}>{p.title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
              {[{ label: 'Enrolled', val: a.total_enrolled, color: T.cyan }, { label: 'Completed', val: a.total_completed, color: T.green }, { label: 'Avg %', val: `${a.avg_progress}%`, color: T.amber }].map(d => (
                <div key={d.label} style={{ background: T.bg4, borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: d.color }}>{d.val}</div>
                  <div style={{ fontSize: 9, color: T.slate, marginTop: 3, letterSpacing: 1 }}>{d.label.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Case Studies Tab ───────────────────────────────────────────────────────────
const STATUS_COLORS = { draft: T.slate, active: T.cyan, completed: T.green, archived: T.amber };
const STATUS_LABELS = { draft: 'DRAFT', active: 'ACTIVE', completed: 'DONE', archived: 'ARCHIVED' };
const PILOT_TEMPLATE = {
  title: '7-Day Mental Wellness Pilot', description: 'A structured 7-day pilot to measure the effect of daily check-ins on mood and stress markers.',
  hypothesis: 'Daily 5-minute mindfulness check-ins over 7 days will improve average mood score by at least 0.5 points.',
  t0_notes: 'BASELINE (Day 0): Record initial sleep, mood, and stress (1-10).', t1_notes: 'MID-POINT (Day 3-4): Record sleep, mood, stress.', t2_notes: 'ENDPOINT (Day 7): Calculate delta from T0. Evaluate hypothesis.',
  duration_days: 7, status: 'draft',
};

function CaseStudiesTab({ uid, lang, isResearcher }) {
  const nl = lang === 'nl';
  const [studies, setStudies] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [err, setErr] = useState('');
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const load = async () => {
    try { const r = await fetch(`${BASE}/lab/case-studies?uid=${encodeURIComponent(uid)}`); setStudies(Array.isArray(await r.json()) ? await r.clone().json() : []); } catch { setStudies([]); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startNew = (tpl = null) => { setForm(tpl || { title:'',description:'',hypothesis:'',t0_notes:'',t1_notes:'',t2_notes:'',status:'draft',duration_days:7,published:false }); setEditing('new'); setErr(''); };
  const openEdit = (s) => { setForm({ ...s }); setEditing(s); setErr(''); };
  const save = async () => {
    if (!form.title?.trim()) { setErr('Title required.'); return; }
    setSaving(true); setErr('');
    try {
      const isNew = editing === 'new';
      const res = await fetch(isNew ? `${BASE}/lab/case-studies` : `${BASE}/lab/case-studies/${editing.id}`, {
        method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, uid }),
      });
      if (!res.ok) throw new Error('Error');
      setEditing(null); load();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };
  const del = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    await fetch(`${BASE}/lab/case-studies/${id}?uid=${encodeURIComponent(uid)}`, { method: 'DELETE' });
    load();
  };
  const runAiAnalysis = async () => {
    if (!form.t0_notes && !form.t1_notes && !form.t2_notes) { setErr('Add measurement notes first.'); return; }
    setAnalyzing(true); setErr('');
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST', headers: CLAUDE_HEADERS,
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001', max_tokens: 600,
          system: 'You are a behavioral research analyst. Analyze the wellness case study measurement notes and provide: key findings, delta assessment, hypothesis verdict, and 2-3 recommendations. Plain text, max 200 words.',
          messages: [{ role: 'user', content: `Study: ${form.title}\nHypothesis: ${form.hypothesis||'N/A'}\n\nT0: ${form.t0_notes||'—'}\nT1: ${form.t1_notes||'—'}\nT2: ${form.t2_notes||'—'}` }],
        }),
      });
      const data = await res.json();
      const analysis = data.content?.[0]?.text || '';
      if (!analysis) throw new Error('No analysis returned');
      f('ai_analysis', analysis);
    } catch (e) { setErr('AI failed: ' + e.message); }
    setAnalyzing(false);
  };

  if (editing !== null) return (
    <div>
      <button style={btn('ghost', { marginBottom: 16, borderRadius: 10 })} onClick={() => setEditing(null)}>← Back</button>
      <div style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.white, marginBottom: 16 }}>{editing === 'new' ? 'New Case Study' : 'Edit Case Study'}</div>
        <input style={{ ...inp(), marginBottom: 10 }} placeholder="Title *" value={form.title||''} onChange={e => f('title', e.target.value)} />
        <textarea style={{ ...inp(), minHeight: 56, resize: 'vertical', marginBottom: 10 }} placeholder="Description" value={form.description||''} onChange={e => f('description', e.target.value)} />
        <textarea style={{ ...inp(), minHeight: 56, resize: 'vertical', marginBottom: 10 }} placeholder="Hypothesis" value={form.hypothesis||''} onChange={e => f('hypothesis', e.target.value)} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <select style={inp()} value={form.status||'draft'} onChange={e => f('status', e.target.value)}>
            {Object.entries(STATUS_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select style={inp()} value={form.duration_days||7} onChange={e => f('duration_days', parseInt(e.target.value))}>
            {[3,5,7,14,21,30].map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
        </div>
        <SectionHead>MEASUREMENT POINTS</SectionHead>
        {[{key:'t0_notes',label:'T0 — Baseline'},{key:'t1_notes',label:'T1 — Mid-point'},{key:'t2_notes',label:'T2 — Endpoint'}].map(({key,label}) => (
          <textarea key={key} style={{ ...inp(), minHeight: 64, resize: 'vertical', marginBottom: 10 }} placeholder={label} value={form[key]||''} onChange={e => f(key, e.target.value)} />
        ))}
        <button style={{ ...btn('primary', { width: '100%', marginBottom: 10, borderRadius: 10 }), background: `${T.purpleL}cc` }} onClick={runAiAnalysis} disabled={analyzing}>
          {analyzing ? 'Analysing…' : '✦ Run AI Analysis'}
        </button>
        {form.ai_analysis && (
          <div style={{ background: T.bg4, border: `1px solid ${T.purpleL}44`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: T.purpleL, fontWeight: 700, marginBottom: 6 }}>AI Analysis</div>
            <div style={{ fontSize: 12, color: T.slate, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{form.ai_analysis}</div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: T.bg4, borderRadius: 10, border: `1px solid ${T.border}`, marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: T.white }}>Publish publicly</span>
          <Toggle value={!!form.published} onChange={v => f('published', v)} />
        </div>
        {err && <div style={{ color: T.red, fontSize: 12, marginBottom: 10 }}>⚠ {err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button style={btn('ghost', { borderRadius: 10 })} onClick={() => setEditing(null)}>Cancel</button>
          <button style={btn('primary', { borderRadius: 10 })} onClick={save} disabled={saving}>{saving ? '…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {isResearcher && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <button style={btn('primary', { borderRadius: 12, padding: '11px 0' })} onClick={() => startNew()}>+ New Study</button>
          <button style={btn('ghost', { borderRadius: 12, padding: '11px 0', fontSize: 12 })} onClick={() => startNew(PILOT_TEMPLATE)}>7-Day Template</button>
        </div>
      )}
      {studies === null ? (
        <div style={{ textAlign: 'center', padding: 48, color: T.slate }}>Loading…</div>
      ) : studies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: T.slate }}>No case studies yet.</div>
      ) : studies.map(study => (
        <div key={study.id} style={{ background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 16px', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                <Badge color={STATUS_COLORS[study.status] || T.slate}>{STATUS_LABELS[study.status] || study.status}</Badge>
                <span style={{ fontSize: 10, color: T.slate }}>{study.duration_days}d</span>
                {study.published && <Badge color={T.green}>PUBLIC</Badge>}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.white, marginBottom: 4 }}>{study.title}</div>
              {study.hypothesis && <div style={{ fontSize: 12, color: T.slate, lineHeight: 1.5 }}>H: {study.hypothesis}</div>}
            </div>
            {isResearcher && (
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button style={btn('ghost', { padding: '6px 12px', borderRadius: 8, fontSize: 11 })} onClick={() => openEdit(study)}>Edit</button>
                <button style={btn('danger', { padding: '6px 12px', borderRadius: 8, fontSize: 11 })} onClick={() => del(study.id)}>Del</button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Lab Screen ────────────────────────────────────────────────────────────
export default function LabScreen({ lang, currentUserId, isResearcher, license, onBack }) {
  const nl = lang === 'nl';
  const canPlay    = license && license !== 'freemium';
  const canEnroll  = canPlay;
  const [tab, setTab] = useState('exercises');
  const [tracks, setTracks]     = useState([]);
  const [programs, setPrograms] = useState([]);
  const [activeTrackId, setActiveTrackId] = useState(null);
  const [trackFilter, setTrackFilter]     = useState('all');

  const loadTracks   = async () => { try { const r = await fetch(`${BASE}/lab/tracks`); const d = await r.json(); setTracks(d.tracks || []); } catch {} };
  const loadPrograms = async () => { try { const r = await fetch(`${BASE}/lab/programs?uid=${encodeURIComponent(currentUserId)}`); const d = await r.json(); setPrograms(d.programs || []); } catch {} };

  useEffect(() => { loadTracks(); loadPrograms(); }, []); // eslint-disable-line

  const filteredTracks = trackFilter === 'all' ? tracks : tracks.filter(t => t.category === trackFilter);
  const handleProgressUpdate = (progId, pct) => setPrograms(prev => prev.map(p => p.id === progId ? { ...p, my_progress: pct } : p));

  const TABS = [
    { id: 'exercises', label: 'Exercises', icon: '🧘' },
    { id: 'games',     label: 'Games',     icon: '🎮' },
    { id: 'programs',  label: 'Programs',  icon: '📋' },
    { id: 'specimens', label: 'Specimens', icon: '🎵' },
    ...(isResearcher ? [{ id: 'research', label: 'Research', icon: '🔬' }] : []),
  ];

  return (
    <div style={{ background: T.bg, minHeight: '100vh', color: T.white, fontFamily: 'Inter,system-ui,sans-serif' }}>
      <style>{`
        .lab-tab-content { animation: labFadeIn 0.2s ease; }
        @keyframes labFadeIn { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:none;} }
      `}</style>

      {/* Header */}
      <div style={{ background: T.bg2, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 50, backdropFilter: 'blur(20px)' }}>
        {/* Purple top accent line */}
        <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${T.purple}, ${T.purpleL}, transparent)` }} />

        <div style={{ padding: '16px 16px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.slate, cursor: 'pointer', fontSize: 20, padding: 0, lineHeight: 1 }}>←</button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.white, letterSpacing: -0.5 }}>
                Sense Lifting <span style={{ color: T.purpleL }}>Lab</span>
              </div>
              <div style={{ fontSize: 11, color: T.slate, marginTop: 2, letterSpacing: 0.5 }}>
                {nl ? 'Oefeningen · Games · Protocollen · Onderzoek' : 'Exercises · Games · Programs · Research'}
              </div>
            </div>
            {isResearcher && <Badge color={T.purpleL}>Researcher</Badge>}
          </div>

          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => { playSound('tab'); setTab(t.id); }} style={{
                flexShrink: 0, background: 'none', border: 'none', padding: '8px 14px 12px', cursor: 'pointer',
                fontSize: 12, fontWeight: 700,
                color: tab === t.id ? T.purpleL : T.slate,
                borderBottom: `2px solid ${tab === t.id ? T.purpleL : 'transparent'}`,
                marginBottom: -1, transition: 'color .2s, border-color .2s',
              }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 16px 100px' }} className="lab-tab-content" key={tab}>

        {/* Freemium gate notice */}
        {!canPlay && tab !== 'exercises' && tab !== 'games' && (
          <div style={{ background: T.bg3, border: `1px solid ${T.amber}44`, borderRadius: 12, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 22 }}>🔒</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.amber, marginBottom: 2 }}>Upgrade Required</div>
              <div style={{ fontSize: 12, color: T.slate }}>{nl ? 'Upgrade naar Starter of hoger voor toegang.' : 'Upgrade to Starter or above to access lab resources.'}</div>
            </div>
          </div>
        )}

        {/* ── EXERCISES ────────────────────────────────────────────────── */}
        {tab === 'exercises' && <ContentTab type="exercise" uid={currentUserId} isResearcher={isResearcher} />}

        {/* ── GAMES ────────────────────────────────────────────────────── */}
        {tab === 'games' && <ContentTab type="game" uid={currentUserId} isResearcher={isResearcher} />}

        {/* ── PROGRAMS ─────────────────────────────────────────────────── */}
        {tab === 'programs' && (
          <div>
            {isResearcher && (
              <>
                <AIProtocolForm uid={currentUserId} lang={lang} tracks={tracks} onCreated={loadPrograms} />
                <ManualProtocolForm uid={currentUserId} lang={lang} tracks={tracks} onCreated={loadPrograms} />
              </>
            )}
            {programs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: T.slate }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.white, marginBottom: 6 }}>No programs yet</div>
                {isResearcher && <div style={{ fontSize: 12 }}>Create your first program above</div>}
              </div>
            ) : programs.map(prog => (
              <ProtocolCard key={prog.id} prog={prog} uid={currentUserId} lang={lang} tracks={tracks}
                onEnrolled={loadPrograms} onProgress={handleProgressUpdate} canEnroll={canEnroll} isResearcher={isResearcher} />
            ))}
          </div>
        )}

        {/* ── SPECIMENS ────────────────────────────────────────────────── */}
        {tab === 'specimens' && (
          <div>
            {isResearcher && <TrackUploadForm uid={currentUserId} lang={lang} onUploaded={loadTracks} />}

            {/* Category filter */}
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 14 }}>
              {['all', ...TRACK_CATS.map(c => c.id)].map(id => (
                <button key={id} onClick={() => setTrackFilter(id)} style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 20, border: `1px solid ${trackFilter === id ? T.cyan + '88' : T.border}`, background: trackFilter === id ? `${T.cyan}15` : T.bg4, color: trackFilter === id ? T.cyan : T.slate, fontSize: 12, fontWeight: trackFilter === id ? 700 : 400, cursor: 'pointer' }}>
                  {id === 'all' ? 'All' : (nl ? TRACK_CATS.find(c=>c.id===id)?.labelNL : id)}
                </button>
              ))}
            </div>

            {filteredTracks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: T.slate }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🎵</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.white, marginBottom: 6 }}>No specimens found</div>
                {isResearcher && <div style={{ fontSize: 12 }}>Add the first specimen above</div>}
              </div>
            ) : filteredTracks.map(track => (
              <SpecimenPlayer key={track.id} track={track} activeId={activeTrackId} onActivate={setActiveTrackId} canPlay={canPlay} lang={lang} uid={currentUserId} />
            ))}
          </div>
        )}

        {/* ── RESEARCH ─────────────────────────────────────────────────── */}
        {tab === 'research' && isResearcher && (
          <div>
            <SectionHead>CASE STUDIES</SectionHead>
            <CaseStudiesTab uid={currentUserId} lang={lang} isResearcher={isResearcher} />
            <SectionHead>RESEARCH DATA</SectionHead>
            <ResearchData tracks={tracks} programs={programs} lang={lang} />
          </div>
        )}
      </div>
    </div>
  );
}
